import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Card from './ui/card';
import Button from './ui/button';
import Input from './ui/input';
import NodesList from './NodesList';
import RelationsList from './RelationsList';
import TestScenariosList from './TestScenariosList';
import NodeDetailsPanel from './NodeDetailsPanel';
import MobileGraphView from './MobileGraphView';
import { FaProjectDiagram, FaListAlt, FaFlask, FaChevronLeft, FaNetworkWired, FaDesktop, FaInfo } from 'react-icons/fa';
import './MobileLabViewSimple.css';

/**
 * Vue mobile simplifiée - Mode "Assistant de consultation"
 * Affichage seulement : Graphique, Nœuds, Relations, Tests
 * Modifications uniquement sur desktop
 */
const MobileLabViewSimple = ({
  user,
  nodes,
  allRelationships,
  testScenarios,
  nodeTests,
  selectedNode,
  setSelectedNode,
  onNodeSelect, // function to perform full node selection (loads relations, nodeTests)
  onBackToDashboard,
  loading
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('graph'); // Commencer par le graphique (valeur ajoutée)
  const [searchTerm, setSearchTerm] = useState('');

  // Navigation simplifiée - consultation uniquement
  const tabs = [
    { id: 'graph', label: t('mobileLabView.tabs.graph'), icon: FaNetworkWired },
    // { id: 'nodes', label: t('mobileLabView.tabs.nodes'), icon: FaProjectDiagram },
    // { id: 'relations', label: t('mobileLabView.tabs.relations'), icon: FaListAlt },
    { id: 'tests', label: t('mobileLabView.tabs.tests'), icon: FaFlask }
  ];

  // Filtrage des nœuds selon le terme de recherche
  const filteredNodes = nodes.filter(node =>
    node.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    node.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // En-tête avec indicateur "consultation seulement"
  const renderMobileHeader = () => (
    <div className="mobile-lab-header">
      <div className="mobile-header-top">
        <Button 
          onClick={onBackToDashboard}
          className="mobile-back-btn"
          variant="ghost"
        >
          <FaChevronLeft /> {t('common.back')}
        </Button>
        <div className="mobile-header-content">
          <h1 className="mobile-lab-title">{t('featureManager.title')}</h1>
          <div className="mobile-view-mode">
            <FaDesktop className="desktop-icon" />
            <span>{t('mobileLabView.viewOnlyMode')}</span>
          </div>
        </div>
      </div>
      
      {/* Message informatif */}
      <div className="mobile-info-banner">
        <FaInfo />
        <span>{t('mobileLabView.editOnDesktop')}</span>
      </div>
      
      {/* Navigation par onglets */}
      <div className="mobile-tab-navigation">
        {tabs.map(tab => {
          const IconComponent = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`mobile-tab ${activeTab === tab.id ? 'active' : ''}`}
            >
              <IconComponent />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );

  // Onglet graphique - valeur ajoutée principale
  const renderGraphTab = () => (
    <div className="mobile-tab-content">
      <div className="mobile-section-header">
        <h3>{t('mobileLabView.graphVisualization')}</h3>
        <p className="mobile-section-description">
          {t('mobileLabView.graphDescription')}
        </p>
      </div>
      
      <MobileGraphView
        nodes={nodes}
        relationships={allRelationships}
        onNodeSelect={onNodeSelect || setSelectedNode}
        selectedNode={selectedNode}
        loading={loading}
        readOnly={true}
        compact={false}
      />
    </div>
  );

  // Onglet nœuds - consultation seulement
  // const renderNodesTab = () => (
  //   <div className="mobile-tab-content">
  //     <div className="mobile-section-header">
  //       <h3>{t('mobileLabView.nodesOverview')}</h3>
  //       <div className="mobile-search-container">
  //         <Input
  //           type="text"
  //           placeholder={t('mobileLabView.searchNodes')}
  //           value={searchTerm}
  //           onChange={(e) => setSearchTerm(e.target.value)}
  //           className="mobile-search-input"
  //         />
  //       </div>
  //       <p className="mobile-nodes-count">
  //         {filteredNodes.length} {t('mobileLabView.nodesFound')}
  //       </p>
  //     </div>
  //
  //     <NodesList
  //       nodes={filteredNodes}
  //       onNodeSelect={setSelectedNode}
  //       selectedNodeId={selectedNode?._id}
  //       loading={loading}
  //       compact={true}
  //       readOnly={true}
  //       relationships={allRelationships}
  //       selectNode={setSelectedNode}
  //       selectedNode={selectedNode}
  //       deleteNode={() => {}} // Pas de suppression en mode consultation
  //       toggleNodeSelection={() => {}} // Pas de sélection multiple en mode consultation
  //     />
  //     
  //     {selectedNode && (
  //       <Card className="mobile-selected-node">
  //         <div className="mobile-node-preview">
  //           <h4>{selectedNode.name}</h4>
  //           <p>{selectedNode.description}</p>
  //           <div className="mobile-node-meta">
  //             <span className="node-type">{selectedNode.type}</span>
  //             <span className="node-status">{selectedNode.status}</span>
  //           </div>
  //         </div>
  //       </Card>
  //     )}
  //   </div>
  // );

  // Onglet relations - consultation seulement
  // const renderRelationsTab = () => (
  //   <div className="mobile-tab-content">
  //     <div className="mobile-section-header">
  //       <h3>{t('mobileLabView.relationshipsOverview')}</h3>
  //       <p className="mobile-relations-count">
  //         {allRelationships.length} {t('mobileLabView.relations')}
  //       </p>
  //     </div>
  //
  //     <RelationsList
  //       relationships={allRelationships}
  //       nodes={nodes}
  //       loading={loading}
  //       compact={true}
  //       readOnly={true}
  //       setExpandedRelation={() => {}} // Pas d'expansion en mode consultation
  //       deleteRelationship={() => {}} // Pas de suppression en mode consultation
  //     />
  //   </div>
  // );

  // Onglet tests - consultation seulement
  const renderTestsTab = () => {
    // Determine which tests to show in the mobile Tests tab:
    // - If a node is selected and `nodeTests` (fetched per-node) is available, show that.
    // - Else if a node is selected, filter global `testScenarios` by scenario.nodeName.
    // - Otherwise show all `testScenarios`.
    let testsToShow = testScenarios || [];

    if (selectedNode) {
      if (nodeTests && nodeTests.length > 0) {
        testsToShow = nodeTests;
      } else {
        testsToShow = (testScenarios || []).filter(s => s.nodeName === selectedNode.name);
      }
    }

    return (
      <div className="mobile-tab-content">
        <div className="mobile-section-header">
          <h3>{t('mobileLabView.testsOverview')}</h3>
          {selectedNode && (
            <p className="mobile-selected-context">
              {t('mobileLabView.testsFor', { nodeName: selectedNode.name })}
            </p>
          )}
        </div>

        <TestScenariosList
          testScenarios={testsToShow}
          duplicateTestIds={[]}
          extractStepsByType={() => []}
          onRemoveDuplicate={() => {}}
          onSelectScenario={() => {}}
          selectedNode={selectedNode}
          loading={loading}
          compact={true}
          readOnly={true}
        />
      </div>
    );
  };

  // Affichage du contenu selon l'onglet actif
  const renderTabContent = () => {
    switch (activeTab) {
      case 'graph':
        return renderGraphTab();
      // case 'nodes':
      //   return renderNodesTab();
      // case 'relations':
      //   return renderRelationsTab();
      case 'tests':
        return renderTestsTab();
      default:
        return renderGraphTab(); // Par défaut le graphique
    }
  };

  return (
    <div className="mobile-lab-view mobile-lab-view--simple">
      {renderMobileHeader()}
      <div className="mobile-lab-content">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default MobileLabViewSimple;
